import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, type Domain } from '../api';
import { DnsRecords } from '../components/DnsRecords';
import { BulbIcon, CheckIcon, PlusIcon, RefreshIcon } from '../components/icons';
import { ErrorNote, PageHeader, Status } from '../components/ui';
import { useSession } from '../session';

const DOMAIN = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** Add a domain in three steps: name it, publish the records, get verified. */
export function AddDomain() {
  const navigate = useNavigate();
  const { team } = useSession();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState<Domain | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const clean = name
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '');
  const looksValid = DOMAIN.test(clean);
  const isRoot = looksValid && clean.split('.').length === 2;
  const shown = domain?.name ?? (looksValid ? clean : 'yourdomain.in');
  const verified = domain?.status === 'verified';
  const step = !domain ? 1 : verified ? 3 : 2;

  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setDomain(await api<Domain>('POST', '/domains', { name: clean }));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function check() {
    if (!domain) return;
    setBusy(true);
    try {
      setDomain(await api<Domain>('POST', `/domains/${encodeURIComponent(domain.id)}/verify`));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const state = (n: number) => (step > n ? 'done' : step === n ? 'current' : 'todo');
  const marker = (n: number) => <span className="marker">{step > n ? <CheckIcon /> : n}</span>;

  return (
    <>
      <PageHeader
        crumbs={
          <>
            <Link to="/domains">Domains</Link>
            <span>/</span>
            <span>Add domain</span>
          </>
        }
        title="Add domain"
        description="Use a domain you own to send email from your own address."
      />

      <ol className="hsteps" aria-label="Steps">
        {[
          { n: 1, title: 'Domain', desc: 'Choose the address' },
          { n: 2, title: 'DNS records', desc: 'Add them at your provider' },
          { n: 3, title: 'Verified', desc: 'Start sending' },
        ].map((s) => (
          <li key={s.n} className={state(s.n)} aria-current={step === s.n ? 'step' : undefined}>
            {marker(s.n)}
            <span>
              <strong>{s.title}</strong>
              <span className="desc">{s.desc}</span>
            </span>
          </li>
        ))}
      </ol>

      {step === 1 ? (
        <div className="wizard">
          <div className="card card-body">
            <h3 className="step-title">Which domain will you send from?</h3>
            <p className="step-desc">The domain or subdomain your emails will come from.</p>
            <form onSubmit={add} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="field">
                <label htmlFor="domain-name">Name</label>
                <input
                  id="domain-name"
                  className="input"
                  placeholder={`mail.${team.slug || 'yourapp'}.in`}
                  autoComplete="off"
                  spellCheck={false}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {name && !looksValid ? (
                  <span className="hint">Enter a domain like mail.yourapp.in.</span>
                ) : null}
              </div>
              {isRoot ? (
                <div className="tip" style={{ marginTop: 0 }}>
                  <BulbIcon />
                  <span>
                    Consider a subdomain such as <code>mail.{clean}</code>. It keeps your product
                    email's reputation separate from your team's everyday email.
                  </span>
                </div>
              ) : null}
              <ErrorNote error={error} />
              <div>
                <button className="btn btn-primary" type="submit" disabled={busy || !looksValid}>
                  <PlusIcon />
                  {busy ? 'Adding…' : 'Add domain'}
                </button>
              </div>
            </form>
          </div>

          <aside className="preview-card" aria-label="Preview">
            <div className="envelope">
              <span className="avatar">Y</span>
              <div className="who">
                <strong>Your Name</strong>
                <span>&lt;hello@{shown}&gt; to me</span>
              </div>
            </div>
            <div className="lines">
              <i style={{ width: '55%' }} />
              <i style={{ width: '85%' }} />
              <i style={{ width: '70%' }} />
              <i style={{ width: '40%' }} />
            </div>
            <div className="foot">
              Once verified, every email is DKIM-signed by {shown}, so inboxes trust it.
            </div>
          </aside>
        </div>
      ) : null}

      {step === 2 && domain ? (
        <div className="card card-body">
          <h3 className="step-title">Add these records for {domain.name}</h3>
          <p className="step-desc">
            Copy each record into your DNS provider, then check. We also check every 10 minutes for
            72 hours.
          </p>
          <DnsRecords domain={domain.name} records={domain.records} />
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 18,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void check()}
            >
              <RefreshIcon />
              {busy ? 'Checking…' : "I've added the records"}
            </button>
            <Link to={`/domains/${domain.id}`} className="btn btn-ghost">
              Finish later
            </Link>
            <Status status={domain.status} />
          </div>
          <ErrorNote error={error} />
        </div>
      ) : null}

      {step === 3 && domain ? (
        <div className="empty">
          <span className="tile">
            <CheckIcon />
          </span>
          <h3>{domain.name} is verified</h3>
          <p>
            You can now send from any address at {domain.name}. Every email is DKIM-signed, so
            inboxes trust it.
          </p>
          <div className="actions">
            <button type="button" className="btn btn-primary" onClick={() => navigate('/emails')}>
              Go to emails
            </button>
            <Link to={`/domains/${domain.id}`} className="btn btn-ghost">
              View domain
            </Link>
          </div>
        </div>
      ) : null}
    </>
  );
}
